/**
 * The address is the state. Every screen is reachable by typing its URL, back and forward do what the
 * menu does, and any screen can be sent to someone else.
 *
 * The app keeps its existing shape — components still read currentLeague, selectedEvent and the page
 * flags out of context — but those are now derived from the path rather than held next to it. One source
 * of truth means the two can never disagree, which is the failure a hand rolled pushState always ends in.
 *
 *   /                                      the grid, league picked by the menu
 *   /l/:group/:sport/:league               a league inside a group
 *   /favorites                             the favourites group
 *   /search                             the games board; /upcoming is the same board, kept for old links
 *   /event/:home/:away/:ko                 every line of one event
 *   /arb/:id                               one opportunity, on its own
 *   /notifications  /notification-settings  /settings
 */
const GROUPS = {
  odds: 'All Sports',
  arbs: 'Arbs',
  value: 'Discrepancies'
}

const GROUP_SLUGS = {
  'All Sports': 'odds',
  Arbs: 'arbs',
  Discrepancies: 'value'
}

const EMPTY_LEAGUE = { group: null, sport: null, league: null }

const part = (value) => encodeURIComponent(String(value))

// a stray percent in a pasted or mangled link is not a reason to take the whole app down, and this runs
// during render where a throw unmounts the root and leaves a blank page
const decode = (piece) => {
  try {
    return decodeURIComponent(piece)
  } catch (e) {
    return piece
  }
}

export const stateFromPath = (pathname) => {
  const [, head, ...rest] = pathname.split('/').map(decode)

  if (head === 'settings') {
    return { page: 'settings', currentLeague: EMPTY_LEAGUE, selectedEvent: null, arbId: null }
  }
  if (head === 'notification-settings') {
    return { page: 'notificationSettings', currentLeague: EMPTY_LEAGUE, selectedEvent: null, arbId: null }
  }
  if (head === 'search') {
    return { page: 'search', currentLeague: EMPTY_LEAGUE, selectedEvent: null, arbId: null }
  }
  if (head === 'upcoming') {
    // the upcoming board is a layout of the search board now; the address it used still opens it
    return { page: 'search', currentLeague: EMPTY_LEAGUE, selectedEvent: null, arbId: null }
  }
  if (head === 'board') {
    return { page: 'board', currentLeague: EMPTY_LEAGUE, selectedEvent: null, arbId: null }
  }
  if (head === 'support') {
    return { page: 'support', currentLeague: EMPTY_LEAGUE, selectedEvent: null, arbId: null }
  }
  if (head === 'notifications') {
    return { page: 'notifications', currentLeague: EMPTY_LEAGUE, selectedEvent: null, arbId: null }
  }
  if (head === 'arb' && rest[0]) {
    return { page: 'arb', currentLeague: EMPTY_LEAGUE, selectedEvent: null, arbId: Number(rest[0]) }
  }
  if (head === 'event' && rest.length >= 2) {
    return {
      page: 'odds',
      currentLeague: EMPTY_LEAGUE,
      selectedEvent: { home: rest[0], away: rest[1], koTime: rest[2] ? Number(rest[2]) : null },
      arbId: null
    }
  }
  if (head === 'favorites') {
    return {
      page: 'odds',
      currentLeague: { group: 'Favorites', sport: null, league: 'Favorites' },
      selectedEvent: null,
      arbId: null
    }
  }
  if (head === 'l' && rest.length >= 3) {
    return {
      page: 'odds',
      currentLeague: { group: GROUPS[rest[0]] || 'All Sports', sport: rest[1], league: rest[2] },
      selectedEvent: null,
      arbId: null
    }
  }

  return { page: 'odds', currentLeague: EMPTY_LEAGUE, selectedEvent: null, arbId: null }
}

export const pathForPage = (page) => ({
  search: '/search',
  support: '/support',
  board: '/board',
  settings: '/settings',
  notificationSettings: '/notification-settings',
  notifications: '/notifications'
}[page] || '/')

export const pathForEvent = (event) => {
  if (!event) {
    return '/'
  }
  return `/event/${part(event.home)}/${part(event.away)}/${event.koTime || ''}`
}

export const pathForLeague = (league) => {
  if (!league || !league.group) {
    return '/'
  }
  if (league.group === 'Favorites') {
    return '/favorites'
  }
  if (!league.sport || !league.league) {
    return '/'
  }
  return `/l/${GROUP_SLUGS[league.group] || 'odds'}/${part(league.sport)}/${part(league.league)}`
}

export const pathForArb = (id) => `/arb/${id}`

export const sameLeague = (a, b) =>
  (a && a.group) === (b && b.group) && (a && a.sport) === (b && b.sport) && (a && a.league) === (b && b.league)
